import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Tv, Film, Megaphone, BarChart3, Users, Heart, Crown, Eye, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const categoryOpts = [
  { value: 'cartoon', label: 'Cartoon' },
  { value: 'live_action', label: 'Live Action' },
];
const eraOpts = [
  { value: 'early_90s', label: 'Early 90s' },
  { value: 'late_90s', label: 'Late 90s' },
  { value: 'early_2000s', label: 'Early 2000s' },
];

function InputField({ label, value, onChange, placeholder, type = 'text', required = false }) {
  return (
    <div>
      <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2 border-2 border-[#0A0A0A] rounded-lg text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border-2 border-[#0A0A0A] rounded-lg text-sm font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[#FF3860] bg-white"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [shows, setShows] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [selectedShow, setSelectedShow] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [users, setUsers] = useState([]);
  const [visitStats, setVisitStats] = useState(null);
  const [visitStatsError, setVisitStatsError] = useState(null);

  const fetchData = useCallback(async () => {
    // Use Promise.allSettled so one failed endpoint doesn't blank the whole page
    const [statsRes, showsRes, commsRes, usersRes, visitsRes] = await Promise.allSettled([
      ax.get(`${API}/stats`),
      ax.get(`${API}/shows`),
      ax.get(`${API}/commercials`),
      ax.get(`${API}/admin/users`),
      ax.get(`${API}/stats/visits`),
    ]);
    if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    if (showsRes.status === 'fulfilled') {
      setShows(showsRes.value.data);
      if (showsRes.value.data.length > 0 && !selectedShow) {
        setSelectedShow(showsRes.value.data[0].id);
      }
    }
    if (commsRes.status === 'fulfilled') setCommercials(commsRes.value.data);
    if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data);
    if (visitsRes.status === 'fulfilled') {
      setVisitStats(visitsRes.value.data);
      setVisitStatsError(null);
    } else {
      const err = visitsRes.reason;
      const msg = err?.response?.status
        ? `Analytics endpoint returned ${err.response.status}. If you just deployed, wait a minute and refresh.`
        : `Could not reach analytics endpoint. Check your deployment is up to date.`;
      setVisitStatsError(msg);
    }
  }, [selectedShow]);

  useEffect(() => { if (isAdmin) fetchData(); }, [isAdmin, fetchData]);

  const fetchEpisodes = useCallback(async () => {
    if (!selectedShow) return;
    try {
      const { data } = await ax.get(`${API}/shows/${selectedShow}/episodes`);
      setEpisodes(data);
    } catch { /* ignore */ }
  }, [selectedShow]);

  useEffect(() => { fetchEpisodes(); }, [fetchEpisodes]);

  const openDialog = (type, item = null) => {
    setDialogType(type);
    setEditItem(item);
    if (type === 'show') {
      setForm(item ? { title: item.title, description: item.description, category: item.category, era: item.era, thumbnail_url: item.thumbnail_url || '' }
        : { title: '', description: '', category: 'cartoon', era: 'late_90s', thumbnail_url: '' });
    } else if (type === 'episode') {
      setForm(item ? { title: item.title, description: item.description, youtube_video_id: item.youtube_video_id, season: item.season, episode_number: item.episode_number, thumbnail_url: item.thumbnail_url || '', show_id: item.show_id }
        : { title: '', description: '', youtube_video_id: '', season: 1, episode_number: 1, thumbnail_url: '', show_id: selectedShow });
    } else {
      setForm(item ? { title: item.title, description: item.description, youtube_video_id: item.youtube_video_id, era: item.era, brand: item.brand || '', thumbnail_url: item.thumbnail_url || '' }
        : { title: '', description: '', youtube_video_id: '', era: 'late_90s', brand: '', thumbnail_url: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (dialogType === 'show') {
        if (editItem) {
          await ax.put(`${API}/shows/${editItem.id}`, form);
          toast.success('Show updated');
        } else {
          await ax.post(`${API}/shows`, form);
          toast.success('Show created');
        }
      } else if (dialogType === 'episode') {
        const payload = { ...form, season: parseInt(form.season), episode_number: parseInt(form.episode_number) };
        if (editItem) {
          await ax.put(`${API}/episodes/${editItem.id}`, payload);
          toast.success('Episode updated');
        } else {
          await ax.post(`${API}/episodes`, payload);
          toast.success('Episode created');
        }
      } else {
        if (editItem) {
          await ax.put(`${API}/commercials/${editItem.id}`, form);
          toast.success('Commercial updated');
        } else {
          await ax.post(`${API}/commercials`, form);
          toast.success('Commercial created');
        }
      }
      setDialogOpen(false);
      fetchData();
      fetchEpisodes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      if (type === 'show') await ax.delete(`${API}/shows/${id}`);
      else if (type === 'episode') await ax.delete(`${API}/episodes/${id}`);
      else await ax.delete(`${API}/commercials/${id}`);
      toast.success('Deleted');
      fetchData();
      fetchEpisodes();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleFamily = async (userId) => {
    try {
      const { data } = await ax.put(`${API}/admin/users/${userId}/family`);
      toast.success(data.is_family ? 'Marked as family member' : 'Removed from family');
      fetchData();
    } catch {
      toast.error('Failed to update user');
    }
  };

  if (!user || !isAdmin) {
    return (
      <div className="page-content min-h-[60vh] flex items-center justify-center">
        <p className="font-['Fredoka'] text-2xl text-[#4B5563]">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="page-content" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-8">
        <h1 className="font-['Fredoka'] text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight mb-8" data-testid="admin-title">
          Admin Panel
        </h1>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="admin-stats">
            {[
              { label: 'Shows', value: stats.shows, icon: Tv, color: '#FF3860' },
              { label: 'Episodes', value: stats.episodes, icon: Film, color: '#4AC6FF' },
              { label: 'Commercials', value: stats.commercials, icon: Megaphone, color: '#FFDE00' },
              { label: 'Users', value: stats.users, icon: BarChart3, color: '#39FF14' },
            ].map(s => (
              <div key={s.label} className="neo-card p-4 bg-white">
                <div className="flex items-center gap-2 mb-2">
                  <s.icon size={16} strokeWidth={2.5} style={{ color: s.color }} />
                  <span className="text-xs font-bold text-[#4B5563] font-['IBM_Plex_Mono'] uppercase">{s.label}</span>
                </div>
                <p className="font-['Fredoka'] text-3xl font-black text-[#0A0A0A]">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Visitor Analytics */}
        <div className="neo-card p-5 mb-8 bg-white" data-testid="visitor-analytics">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Eye size={18} strokeWidth={2.5} className="text-[#FF3860]" />
            <h2 className="font-['Fredoka'] text-xl font-bold text-[#0A0A0A]">Visitor Analytics</h2>
            <span className="text-xs text-[#4B5563] font-['IBM_Plex_Mono'] uppercase tracking-wider">privacy-friendly · no cookies</span>
          </div>
          {!visitStats && visitStatsError && (
            <div className="p-4 rounded-lg border-2 border-[#0A0A0A] bg-[#FFDE00]/40 font-['Nunito'] text-sm text-[#0A0A0A]" data-testid="visit-stats-error">
              {visitStatsError}
            </div>
          )}
          {!visitStats && !visitStatsError && (
            <div className="p-4 rounded-lg border-2 border-dashed border-[#0A0A0A]/40 bg-[#FDFBEC] font-['Nunito'] text-sm text-[#4B5563]">
              Loading visitor stats…
            </div>
          )}
          {visitStats && (
            <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                { label: 'Total Visits', value: visitStats.total_visits, color: '#FF3860' },
                { label: 'Today', value: visitStats.today, color: '#39FF14' },
                { label: 'Last 7 Days', value: visitStats.last_7_days, color: '#4AC6FF' },
                { label: 'Last 30 Days', value: visitStats.last_30_days, color: '#FFDE00' },
              ].map(v => (
                <div key={v.label} className="rounded-lg border-2 border-[#0A0A0A] p-3 bg-[#FDFBEC]" data-testid={`visit-${v.label.toLowerCase().replace(/ /g, '-')}`}>
                  <p className="text-xs font-bold text-[#4B5563] font-['IBM_Plex_Mono'] uppercase">{v.label}</p>
                  <p className="font-['Fredoka'] text-2xl font-black" style={{ color: v.color }}>{(v.value ?? 0).toLocaleString()}</p>
                </div>
              ))}
            </div>

            {/* Daily bars */}
            <h3 className="font-['Fredoka'] text-sm font-bold text-[#0A0A0A] mb-2 flex items-center gap-2">
              <TrendingUp size={14} strokeWidth={2.5} /> Last 7 Days
            </h3>
            <div className="flex items-end gap-2 h-32 mb-5 border-b-2 border-[#0A0A0A] pb-2">
              {(() => {
                const daily = visitStats.daily || [];
                const max = Math.max(1, ...daily.map(d => d.count));
                return daily.map((d) => (
                  <div key={d.iso_date} className="flex-1 flex flex-col items-center gap-1">
                    <div className="text-[10px] font-bold font-['IBM_Plex_Mono'] text-[#0A0A0A]">{d.count}</div>
                    <div
                      className="w-full rounded-t border-2 border-[#0A0A0A] bg-[#FF3860]"
                      style={{ height: `${Math.max(6, (d.count / max) * 90)}px` }}
                      title={`${d.iso_date}: ${d.count} visits`}
                    />
                    <div className="text-[10px] font-bold font-['IBM_Plex_Mono'] text-[#4B5563]">{d.date}</div>
                  </div>
                ));
              })()}
            </div>

            {/* Top paths */}
            {(visitStats.top_paths?.length || 0) > 0 && (
              <>
                <h3 className="font-['Fredoka'] text-sm font-bold text-[#0A0A0A] mb-2">Most-Visited Pages (last 30 days)</h3>
                <div className="space-y-1">
                  {visitStats.top_paths.map((p, i) => (
                    <div key={p.path} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#FDFBEC] border border-[#0A0A0A]/20" data-testid={`top-path-${i}`}>
                      <span className="font-['IBM_Plex_Mono'] text-sm text-[#0A0A0A] truncate">{p.path}</span>
                      <span className="font-['Fredoka'] font-bold text-sm text-[#FF3860]">{p.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            </>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="shows" className="w-full">
          <TabsList className="mb-6 bg-white border-2 border-[#0A0A0A] rounded-xl p-1 shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]">
            <TabsTrigger value="shows" className="font-['Fredoka'] font-bold data-[state=active]:bg-[#FF3860] data-[state=active]:text-white rounded-lg" data-testid="tab-shows">Shows</TabsTrigger>
            <TabsTrigger value="episodes" className="font-['Fredoka'] font-bold data-[state=active]:bg-[#4AC6FF] data-[state=active]:text-[#0A0A0A] rounded-lg" data-testid="tab-episodes">Episodes</TabsTrigger>
            <TabsTrigger value="commercials" className="font-['Fredoka'] font-bold data-[state=active]:bg-[#FFDE00] data-[state=active]:text-[#0A0A0A] rounded-lg" data-testid="tab-commercials">Commercials</TabsTrigger>
            <TabsTrigger value="users" className="font-['Fredoka'] font-bold data-[state=active]:bg-[#39FF14] data-[state=active]:text-[#0A0A0A] rounded-lg" data-testid="tab-users">Users</TabsTrigger>
          </TabsList>

          {/* Shows Tab */}
          <TabsContent value="shows">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Fredoka'] text-xl font-bold">Manage Shows</h2>
              <button onClick={() => openDialog('show')} className="neo-btn flex items-center gap-1 px-4 py-2 rounded-lg bg-[#FF3860] text-white text-sm" data-testid="add-show-btn">
                <Plus size={16} strokeWidth={2.5} /> Add Show
              </button>
            </div>
            <div className="space-y-2">
              {shows.map(show => (
                <div key={show.id} className="neo-card flex items-center justify-between p-4 bg-white" data-testid={`admin-show-${show.id}`}>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A] truncate">{show.title}</h3>
                    <p className="text-xs text-[#4B5563] font-['IBM_Plex_Mono']">{show.category} / {show.era}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={() => openDialog('show', show)} className="p-2 rounded-lg border-2 border-[#0A0A0A] bg-[#FFDE00] hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-shadow" data-testid={`edit-show-${show.id}`}>
                      <Pencil size={14} strokeWidth={2.5} />
                    </button>
                    <button onClick={() => handleDelete('show', show.id)} className="p-2 rounded-lg border-2 border-[#0A0A0A] bg-[#FF007F] text-white hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-shadow" data-testid={`delete-show-${show.id}`}>
                      <Trash2 size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Episodes Tab */}
          <TabsContent value="episodes">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <h2 className="font-['Fredoka'] text-xl font-bold">Manage Episodes</h2>
                <select
                  value={selectedShow}
                  onChange={(e) => setSelectedShow(e.target.value)}
                  className="px-3 py-1.5 border-2 border-[#0A0A0A] rounded-lg text-sm font-['Nunito'] bg-white"
                  data-testid="episode-show-select"
                >
                  {shows.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </div>
              <button onClick={() => openDialog('episode')} className="neo-btn flex items-center gap-1 px-4 py-2 rounded-lg bg-[#4AC6FF] text-[#0A0A0A] text-sm" data-testid="add-episode-btn">
                <Plus size={16} strokeWidth={2.5} /> Add Episode
              </button>
            </div>
            <div className="space-y-2">
              {episodes.map(ep => (
                <div key={ep.id} className="neo-card flex items-center justify-between p-4 bg-white" data-testid={`admin-episode-${ep.id}`}>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A] truncate">S{ep.season}E{ep.episode_number} - {ep.title}</h3>
                    <p className="text-xs text-[#4B5563] font-['IBM_Plex_Mono']">YouTube: {ep.youtube_video_id}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={() => openDialog('episode', ep)} className="p-2 rounded-lg border-2 border-[#0A0A0A] bg-[#FFDE00] hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-shadow" data-testid={`edit-episode-${ep.id}`}>
                      <Pencil size={14} strokeWidth={2.5} />
                    </button>
                    <button onClick={() => handleDelete('episode', ep.id)} className="p-2 rounded-lg border-2 border-[#0A0A0A] bg-[#FF007F] text-white hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-shadow" data-testid={`delete-episode-${ep.id}`}>
                      <Trash2 size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              ))}
              {episodes.length === 0 && (
                <p className="text-center py-8 text-[#4B5563] font-['Nunito']">No episodes for this show</p>
              )}
            </div>
          </TabsContent>

          {/* Commercials Tab */}
          <TabsContent value="commercials">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Fredoka'] text-xl font-bold">Manage Commercials</h2>
              <button onClick={() => openDialog('commercial')} className="neo-btn flex items-center gap-1 px-4 py-2 rounded-lg bg-[#FFDE00] text-[#0A0A0A] text-sm" data-testid="add-commercial-btn">
                <Plus size={16} strokeWidth={2.5} /> Add Commercial
              </button>
            </div>
            <div className="space-y-2">
              {commercials.map(comm => (
                <div key={comm.id} className="neo-card flex items-center justify-between p-4 bg-white" data-testid={`admin-commercial-${comm.id}`}>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A] truncate">{comm.title}</h3>
                    <p className="text-xs text-[#4B5563] font-['IBM_Plex_Mono']">{comm.brand} / {comm.era} / YT: {comm.youtube_video_id}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={() => openDialog('commercial', comm)} className="p-2 rounded-lg border-2 border-[#0A0A0A] bg-[#FFDE00] hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-shadow" data-testid={`edit-commercial-${comm.id}`}>
                      <Pencil size={14} strokeWidth={2.5} />
                    </button>
                    <button onClick={() => handleDelete('commercial', comm.id)} className="p-2 rounded-lg border-2 border-[#0A0A0A] bg-[#FF007F] text-white hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-shadow" data-testid={`delete-commercial-${comm.id}`}>
                      <Trash2 size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <h2 className="font-['Fredoka'] text-xl font-bold mb-4">Manage Users</h2>
            <div className="space-y-2">
              {users.map(u => (
                <div key={u._id} className="neo-card flex items-center justify-between p-4 bg-white" data-testid={`admin-user-${u._id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A]">{u.name || u.email}</h3>
                      {u.role === 'admin' && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-[#FF3860] text-white">ADMIN</span>}
                      {u.is_family && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-[#FF007F] text-white">FAMILY</span>}
                    </div>
                    <p className="text-xs text-[#4B5563] font-['IBM_Plex_Mono']">{u.email}</p>
                  </div>
                  {u.role !== 'admin' && (
                    <button
                      onClick={() => toggleFamily(u._id)}
                      className={`neo-btn flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${u.is_family ? 'bg-[#FF007F] text-white' : 'bg-white text-[#0A0A0A]'}`}
                      data-testid={`toggle-family-${u._id}`}
                    >
                      <Heart size={14} strokeWidth={2.5} fill={u.is_family ? 'currentColor' : 'none'} />
                      {u.is_family ? 'Family' : 'Set Family'}
                    </button>
                  )}
                </div>
              ))}
              {users.length === 0 && (
                <p className="text-center py-8 text-[#4B5563] font-['Nunito']">No users yet</p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] max-w-lg" data-testid="admin-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Fredoka'] text-xl">
                {editItem ? 'Edit' : 'Add'} {dialogType === 'show' ? 'Show' : dialogType === 'episode' ? 'Episode' : 'Commercial'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <InputField label="Title" value={form.title || ''} onChange={v => setForm(f => ({...f, title: v}))} placeholder="Enter title" required />
              <InputField label="Description" value={form.description || ''} onChange={v => setForm(f => ({...f, description: v}))} placeholder="Enter description" />

              {dialogType === 'show' && (
                <>
                  <SelectField label="Category" value={form.category || 'cartoon'} onChange={v => setForm(f => ({...f, category: v}))} options={categoryOpts} />
                  <SelectField label="Era" value={form.era || 'late_90s'} onChange={v => setForm(f => ({...f, era: v}))} options={eraOpts} />
                  <InputField label="Thumbnail URL" value={form.thumbnail_url || ''} onChange={v => setForm(f => ({...f, thumbnail_url: v}))} placeholder="https://..." />
                </>
              )}
              {dialogType === 'episode' && (
                <>
                  <InputField label="YouTube Video ID" value={form.youtube_video_id || ''} onChange={v => setForm(f => ({...f, youtube_video_id: v}))} placeholder="e.g. dQw4w9WgXcQ" required />
                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="Season" value={form.season || 1} onChange={v => setForm(f => ({...f, season: v}))} type="number" />
                    <InputField label="Episode #" value={form.episode_number || 1} onChange={v => setForm(f => ({...f, episode_number: v}))} type="number" />
                  </div>
                  <InputField label="Thumbnail URL" value={form.thumbnail_url || ''} onChange={v => setForm(f => ({...f, thumbnail_url: v}))} placeholder="https://..." />
                </>
              )}
              {dialogType === 'commercial' && (
                <>
                  <InputField label="YouTube Video ID" value={form.youtube_video_id || ''} onChange={v => setForm(f => ({...f, youtube_video_id: v}))} placeholder="e.g. dQw4w9WgXcQ" required />
                  <InputField label="Brand" value={form.brand || ''} onChange={v => setForm(f => ({...f, brand: v}))} placeholder="e.g. Nickelodeon" />
                  <SelectField label="Era" value={form.era || 'late_90s'} onChange={v => setForm(f => ({...f, era: v}))} options={eraOpts} />
                  <InputField label="Thumbnail URL" value={form.thumbnail_url || ''} onChange={v => setForm(f => ({...f, thumbnail_url: v}))} placeholder="https://..." />
                </>
              )}
            </div>
            <DialogFooter>
              <button onClick={() => setDialogOpen(false)} className="neo-btn px-4 py-2 rounded-lg bg-white text-sm" data-testid="dialog-cancel-btn">Cancel</button>
              <button onClick={handleSave} className="neo-btn px-4 py-2 rounded-lg bg-[#FF3860] text-white text-sm" data-testid="dialog-save-btn">
                {editItem ? 'Update' : 'Create'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
